import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Download, Save, Trash2, BarChart3 } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const iso = (d) => d.toISOString().slice(0, 10);
const cell = (r, c) => { const v = r[c.k]; if (v == null || v === "") return "—"; if (c.money) return money(v); return `${v}${c.suffix || ""}`; };

export default function ReportingCenter() {
  const { level } = useAuth();
  const nav = useNavigate();
  const isAdmin = level >= 80;
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState("annualized_premium_carrier_lob");
  const [activeTitle, setActiveTitle] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [producers, setProducers] = useState([]);
  const [views, setViews] = useState([]);
  const t = new Date();
  const [f, setF] = useState({ eff_from: `${t.getFullYear()}-01-01`, eff_to: iso(t), producer: "", carrier: "", lob: "", status: "" });

  useEffect(() => {
    api.get("/reporting/catalog").then(({ data }) => setCatalog(data.sections)).catch(() => toast.error("Catalog failed"));
    api.get("/reporting/saved-views").then(({ data }) => setViews(data.items)).catch(() => {});
    if (isAdmin) api.get("/dashboard/agency").then(({ data }) => setProducers(data.producers || [])).catch(() => {});
  }, [isAdmin]);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [f]);

  const runReport = useCallback((key) => {
    setActive(key); setLoading(true); setData(null);
    api.get(`/reporting/run/${key}?${qs()}`).then(({ data }) => { setData(data); setActiveTitle(data.title); }).catch(() => toast.error("Report failed")).finally(() => setLoading(false));
  }, [qs]);

  useEffect(() => { runReport(active); /* eslint-disable-next-line */ }, [qs]);

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/reporting/export/${active}?format=${fmt}&${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${active}.${fmt}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported (scoped to your access).`);
    } catch { toast.error("Export failed."); }
  };
  const saveView = async () => {
    const name = window.prompt("Save this report + filters as:", activeTitle);
    if (!name) return;
    try { const { data } = await api.post("/reporting/saved-views", { name, report_key: active, filters: f }); setViews([data, ...views]); toast.success("Saved."); }
    catch { toast.error("Save failed."); }
  };
  const applyView = (v) => { setF({ ...f, ...v.filters }); setActive(v.report_key); setTimeout(() => runReport(v.report_key), 0); };
  const delView = async (v) => { await api.delete(`/reporting/saved-views/${v.id}`); setViews(views.filter((x) => x.id !== v.id)); };

  const drill = (r) => { if (data?.drill_route) nav(r.party_id && data.drill_route === "/clients" ? `/clients/${r.party_id}` : data.drill_route); };
  const Table = ({ columns, rows }) => (
    <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
      <table className="w-full text-sm">
        <thead><tr className="bg-cloud border-b border-border">{columns.map((c) => <th key={c.k} className="text-left px-3 py-2 text-[11px] uppercase text-navy font-semibold whitespace-nowrap">{c.h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-mutedsea">No records for the current scope/filters.</td></tr> :
            rows.map((r, i) => (
              <tr key={i} data-testid={`report-row-${i}`} onClick={() => drill(r)} className={`border-b border-border last:border-0 ${data?.drill_route ? "hover:bg-ice cursor-pointer nav-link" : ""}`}>
                {columns.map((c) => <td key={c.k} className="px-3 py-2 whitespace-nowrap text-slate">{cell(r, c)}</td>)}
              </tr>))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 animate-fade-in" data-testid="reporting-center">
      <PageHeader title="Reporting Center" subtitle="All agency reports in one place — scoped to your access, with drill-downs and export.">
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="date" data-testid="rpt-from" value={f.eff_from} onChange={(e) => setF({ ...f, eff_from: e.target.value })} className="h-9 w-36" />
          <span className="text-mutedsea text-sm">to</span>
          <Input type="date" data-testid="rpt-to" value={f.eff_to} onChange={(e) => setF({ ...f, eff_to: e.target.value })} className="h-9 w-36" />
          {isAdmin && (
            <Select value={f.producer || "all"} onValueChange={(v) => setF({ ...f, producer: v === "all" ? "" : v })}>
              <SelectTrigger className="w-44 h-9" data-testid="rpt-producer"><SelectValue placeholder="All Producers" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Producers</SelectItem>{producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Input placeholder="Carrier" data-testid="rpt-carrier" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} className="h-9 w-28" />
          <Input placeholder="LOB" data-testid="rpt-lob" value={f.lob} onChange={(e) => setF({ ...f, lob: e.target.value })} className="h-9 w-24" />
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="space-y-3" data-testid="report-catalog">
          {catalog.map((s) => (
            <div key={s.key} className="bg-white border border-border rounded-sm overflow-hidden">
              <div className="px-3 py-2 bg-navy text-white text-xs uppercase tracking-wide font-semibold">{s.label}</div>
              <div className="divide-y divide-border">
                {s.reports.map((r) => (
                  <button key={r.key} data-testid={`report-${r.key}`} onClick={() => runReport(r.key)}
                    className={`w-full text-left px-3 py-2 text-sm nav-link ${active === r.key ? "bg-ice text-atlantic font-medium" : "text-slate hover:bg-cloud"}`}>{r.title}</button>
                ))}
              </div>
            </div>
          ))}
          {views.length > 0 && (
            <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="report-saved-views">
              <div className="px-3 py-2 bg-atlantic text-white text-xs uppercase tracking-wide font-semibold">My Saved Reports</div>
              <div className="divide-y divide-border">
                {views.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-cloud">
                    <button onClick={() => applyView(v)} className="text-slate text-left nav-link truncate" data-testid={`saved-view-${v.id}`}>{v.name}</button>
                    <button onClick={() => delView(v)} className="text-mutedsea hover:text-seared"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-navy flex items-center gap-2"><BarChart3 className="h-5 w-5 text-atlantic" /> {activeTitle || "Report"}</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={saveView} data-testid="rpt-save-view" className="border-atlantic text-atlantic"><Save className="h-3.5 w-3.5 mr-1" /> Save View</Button>
              <Button size="sm" variant="outline" onClick={() => exportFile("csv")} data-testid="rpt-export" className="border-navy text-navy"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} data-testid="rpt-export-xlsx" className="border-navy text-navy"><Download className="h-3.5 w-3.5 mr-1" /> XLSX</Button>
              <Button size="sm" onClick={() => exportFile("pdf")} data-testid="rpt-export-pdf" className="bg-navy hover:bg-atlantic text-white"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
            </div>
          </div>

          {loading ? <div className="text-mutedsea py-8">Running report…</div> : !data ? null : (
            <>
              {data.no_feed && <div className="bg-seaamber/10 border border-seaamber/30 text-seaamber rounded-sm px-4 py-3 text-sm" data-testid="rpt-no-feed">{data.message || "NO LIVE FEED CONNECTED."}</div>}
              {data.kpis && data.kpis.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rpt-kpis">
                  {data.kpis.map((k, i) => (
                    <div key={i} className="border border-border rounded-sm p-4 bg-white">
                      <div className="text-[11px] uppercase tracking-wide text-mutedsea">{k.label}</div>
                      <div className="font-serif text-2xl mt-1 text-navy">{k.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {data.sections && data.sections.map((s, i) => (
                <div key={i} className="space-y-1"><div className="text-xs uppercase tracking-wide text-navy font-semibold">{s.title}</div><Table columns={s.columns} rows={s.rows} /></div>
              ))}
              {data.columns && data.columns.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs text-mutedsea" data-testid="rpt-row-count">{data.rows.length} record(s){data.drill_route ? " · click a row to open" : ""}</div>
                  <Table columns={data.columns} rows={data.rows} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
